import { ArrowLeft, Calendar, Moon, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import { get } from "@/services/axiosUitils";
import welcomeBg from "../../assets/homebg.png";

const SleepDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sleepData, setSleepData] = useState<any[]>([]);

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;
  const selectedDateObj = location.state?.selectedDateObj || null;

  const [summaryData, setSummaryData] = useState({
    stages: {
      deep: 0.0,
      light: 0.0,
      rem: 0.0,
      wake: 0.0,
    },
    totalHoursAsleep: 0.0,
    sleep_hours: 0.0,
    days: 7,
    weeks: 4,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Fetch sleep data from API
  const fetchSleepData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/sleep-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl, {
        headers: {
          "X-Tenant-Id": "ab9bb443-b2ef-4b55-b0ed-0b4a41bb2dc8",
        },
      });

      // Handle both boolean and object success formats
      const isSuccess =
        typeof response.success === "boolean"
          ? response.success
          : response.success?.code === "Retrieved";

      if (isSuccess && response.summary) {
        setSummaryData(response.summary);
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        // Transform API data to match chart format
        if (type === "weekly") {
          const transformedData = response.graph.map((item: any) => ({
            day: item.day,
            date: item.date, // Start capturing date
            hours: item.value,
            // For weekly data, we'll use the total hours for now
            // In a real scenario, you might have separate endpoints for sleep stages
            deep: item.value * 0.25, // ~25% deep sleep
            light: item.value * 0.6, // ~60% light sleep
            rem: item.value * 0.15, // ~15% REM sleep
          }));
          setSleepData(transformedData);
        } else {
          const transformedData = response.graph.map((item: any) => ({
            week: item.week,
            hours: item.value,
          }));
          setSleepData(transformedData);
        }
      } else {
        setError("Failed to fetch sleep data");
        setSleepData([]);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch sleep data");
      setSleepData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: "weekly" | "monthly") => {
    setActiveTab(tab);
    fetchSleepData(tab);
  };

  // Load initial data
  useEffect(() => {
    fetchSleepData("weekly");
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      return "";
    }
    const startDate = new Date(dateRange.start_date).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
      }
    );
    const endDate = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${startDate} - ${endDate}`;
  };

  // Get current night's sleep data
  const getCurrentSleepData = () => {
    const hours =
      summaryData.totalHoursAsleep || summaryData.sleep_hours || 0.0;
    const deep = summaryData.stages?.deep || 0.0;
    const light = summaryData.stages?.light || 0.0;
    const rem = summaryData.stages?.rem || 0.0;
    const wake = summaryData.stages?.wake || 0.0;

    return {
      hours: hours,
      deep: deep,
      light: light,
      rem: rem,
      wake: wake,
    };
  };

  const currentSleep = getCurrentSleepData();

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="z-50 rounded-xl border-2 border-gray-300 bg-white p-3 shadow-2xl dark:border-gray-600 dark:bg-gray-800">
          <p className="mb-2 font-bold text-gray-900 dark:text-white">
            {data.date ? data.date : label}
          </p>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
              Total: {data.hours?.toFixed(1)} hrs
            </p>
            {/* Show stages breakdown if available */}
            {(data.deep > 0 || data.light > 0 || data.rem > 0) && (
              <div className="mt-2 space-y-1 border-t border-gray-200 pt-2 text-xs dark:border-gray-600">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600 dark:text-gray-400">Deep:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{data.deep?.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600 dark:text-gray-400">Light:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{data.light?.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600 dark:text-gray-400">REM:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{data.rem?.toFixed(1)}h</span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">Sleep</h1>
            <p className="text-xs text-muted-foreground">
              {selectedDate ? selectedDate : "Your rest and recovery"}
            </p>
          </div>
          <div className="w-10" />
        </div>

        <Card className="rounded-2xl border border-border bg-gradient-to-br from-blue-50 to-blue-100 p-5 shadow-lg dark:from-blue-950 dark:to-blue-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-500/20">
              <Moon className="h-7 w-7 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="text-3xl font-bold text-foreground">
                {loading
                  ? "..."
                  : currentSleep.hours === 0
                    ? "0.0"
                    : currentSleep.hours.toFixed(1)}
              </div>
              <p className="text-sm text-muted-foreground">hours last night</p>
              {formatDateRange() && (
                <p className="text-xs text-gray-500">{formatDateRange()}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 border-t border-blue-200/50 pt-3">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {loading
                  ? "..."
                  : currentSleep.deep === 0
                    ? "0.0h"
                    : `${currentSleep.deep.toFixed(1)}h`}
              </div>
              <div className="text-xs text-muted-foreground">Deep</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-yellow-600">
                {loading
                  ? "..."
                  : currentSleep.light === 0
                    ? "0.0h"
                    : `${currentSleep.light.toFixed(1)}h`}
              </div>
              <div className="text-xs text-muted-foreground">Light</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                {loading
                  ? "..."
                  : currentSleep.rem === 0
                    ? "0.0h"
                    : `${currentSleep.rem.toFixed(1)}h`}
              </div>
              <div className="text-xs text-muted-foreground">REM</div>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-blue-50/30 p-4 shadow-lg dark:bg-blue-950/30">
          <div className="flex gap-3">
            <TrendingUp className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">
                About Sleep Tracking
              </h4>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Sleep stages are tracked using heart rate variability and
                movement patterns. Deep sleep is crucial for physical recovery,
                while REM sleep supports memory and learning. Adults need 7-9
                hours nightly.
              </p>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-card shadow-lg">
          <div className="p-4">
            <div className="inline-flex w-full max-w-sm rounded-xl bg-muted p-1">
              <button
                onClick={() => handleTabChange("weekly")}
                disabled={loading}
                className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 disabled:opacity-50 ${activeTab === "weekly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/50"
                  }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handleTabChange("monthly")}
                disabled={loading}
                className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 disabled:opacity-50 ${activeTab === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/50"
                  }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="space-y-4 px-4 pb-4 pt-0">
            {activeTab === "weekly" && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">
                    This Week's Sleep ({sleepData.length}{" "}
                    {sleepData.length === 1 ? "day" : "days"})
                  </h3>
                  {loading && (
                    <div className="text-sm text-primary">Loading...</div>
                  )}
                </div>

                {error ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <div className="mb-2 text-red-500">⚠️</div>
                    <div className="text-sm text-red-600">{error}</div>
                    <button
                      onClick={() => fetchSleepData("weekly")}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      Try Again
                    </button>
                  </div>
                ) : sleepData.length === 0 && !loading ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <Moon className="mb-2 h-12 w-12 text-gray-300" />
                    <div className="text-sm text-gray-500">
                      No sleep data available
                    </div>
                  </div>
                ) : sleepData.every(item => item.hours === 0) ? (
                  <div className="flex flex-col gap-2">
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sleepData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            opacity={0.2}
                          />
                          <XAxis
                            dataKey="day"
                            stroke="hsl(var(--muted-foreground))"
                            fontSize={12}
                            tick={{ fill: "hsl(var(--muted-foreground))" }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            stroke="hsl(var(--muted-foreground))"
                            fontSize={12}
                            domain={[0, 1]}
                            tick={{ fill: "hsl(var(--muted-foreground))" }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="hours"
                            stroke="hsl(var(--muted))"
                            strokeWidth={2}
                            dot={{ r: 0 }}
                            activeDot={{ r: 4 }}
                            strokeDasharray="5 5"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <span className="inline-block h-0.5 w-3 bg-gray-300" />
                        No sleep data recorded this week
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sleepData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          opacity={0.2}
                        />
                        <XAxis
                          dataKey="day"
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                          axisLine={false}
                          domain={[
                            0,
                            Math.max(...sleepData.map(d => d.hours), 10),
                          ]}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="hours"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ fill: "#3b82f6", r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {activeTab === "monthly" && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">
                    This Month's Sleep ({sleepData.length}{" "}
                    {sleepData.length === 1 ? "week" : "weeks"})
                  </h3>
                  {loading && (
                    <div className="text-sm text-primary">Loading...</div>
                  )}
                </div>

                {error ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <div className="mb-2 text-red-500">⚠️</div>
                    <div className="text-sm text-red-600">{error}</div>
                    <button
                      onClick={() => fetchSleepData("monthly")}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      Try Again
                    </button>
                  </div>
                ) : sleepData.length === 0 && !loading ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <Moon className="mb-2 h-12 w-12 text-gray-300" />
                    <div className="text-sm text-muted-foreground">
                      No sleep data available
                    </div>
                  </div>
                ) : sleepData.every(item => item.hours === 0) ? (
                  <div className="flex flex-col gap-2">
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sleepData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            opacity={0.2}
                          />
                          <XAxis
                            dataKey="week"
                            stroke="hsl(var(--muted-foreground))"
                            fontSize={12}
                            tick={{ fill: "hsl(var(--muted-foreground))" }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            stroke="hsl(var(--muted-foreground))"
                            fontSize={12}
                            domain={[0, 1]}
                            tick={{ fill: "hsl(var(--muted-foreground))" }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="hours"
                            stroke="hsl(var(--muted))"
                            strokeWidth={2}
                            dot={{ r: 0 }}
                            activeDot={{ r: 4 }}
                            strokeDasharray="5 5"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <span className="inline-block h-0.5 w-3 bg-gray-300" />
                        No sleep data recorded this month
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sleepData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          opacity={0.2}
                        />
                        <XAxis
                          dataKey="week"
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                          axisLine={false}
                          domain={[
                            0,
                            Math.max(...sleepData.map(d => d.hours), 60),
                          ]}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="hours"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ fill: "#3b82f6", r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SleepDetail;
